/**
 * Zod request schemas for tenant, membership and provisioning endpoints.
 *
 * Kept in its own file rather than appended to `api.ts` because these are the
 * schemas that decide who exists and what they may do — the ones worth being
 * able to read end to end without scrolling past exam and tutor payloads.
 *
 * A recurring shape here: none of these accept `tenantId`, `role`, `board`,
 * `grade` or `school` as authority. Where an id appears it selects a CANDIDATE
 * the server then re-loads and re-authorizes against the resolved request
 * context. A body that could name its own tenant would make the tenant header a
 * suggestion rather than a boundary.
 */

import { z } from 'zod';
import { zBoard, zGrade } from './api';
import {
  LEVEL_CODE_RE,
  PASSWORD_MIN_LENGTH,
  SIGNUP_POLICIES,
  isValidTenantSlug,
  isWellFormedTenantSlug,
  normalizeLoginIdentifier,
} from '../constants';

/**
 * A tenant slug, validated with the same predicate the resolver uses.
 *
 * Bounded to DNS label rules because the slug must work identically as
 * `/school/:slug` and as `:slug.domain.com` — a value legal in one form and not
 * the other would resolve through one URL and 404 through its alias.
 */
export const zTenantSlug = z.string().trim().toLowerCase().refine(isWellFormedTenantSlug, {
  message: 'Use 2–63 lowercase letters, numbers and single hyphens.',
});

/**
 * A slug being CLAIMED by a new tenant. Shape, plus the reserved list.
 *
 * Deliberately a second schema rather than a stricter default: the reserved
 * check belongs to assignment only. `zTenantSlug` above answers "could this
 * name a tenant?", which is what every lookup needs — and `cwy`, the
 * first-party workspace's own slug, is reserved AND real.
 */
export const zNewTenantSlug = z.string().trim().toLowerCase().refine(isValidTenantSlug, {
  message: 'Use 2–63 lowercase letters, numbers and single hyphens. Some names are reserved.',
});

/**
 * A school-local login identifier, normalized at the boundary.
 *
 * Normalizing here — inside the schema, not in the route — is what guarantees
 * creation and login agree. If one path normalized and the other did not, a
 * student provisioned as "  1234 " could never sign in by typing "1234", and
 * the failure would present as a wrong password.
 */
export const zLoginIdentifier = z
  .string()
  .min(1)
  .max(128)
  .transform((raw) => normalizeLoginIdentifier(raw))
  .refine((value): value is string => value !== null, {
    message: 'Enter a valid ID.',
  });

/**
 * A password chosen by a person.
 *
 * Length only. Composition rules (an uppercase, a digit, a symbol) push people
 * toward shorter passwords that satisfy a checklist, and a school student
 * typing on a shared machine is exactly who they hurt most.
 */
export const zPassword = z.string().min(PASSWORD_MIN_LENGTH).max(200);

// ── Public tenant resolution ────────────────────────────────────────────────

export const tenantResolveQuery = z.object({
  slug: zTenantSlug,
});

// ── Platform: tenant management ─────────────────────────────────────────────

/**
 * The boards a school teaches — at creation, and when CWY edits them later. One
 * definition, so the create form and the edit panel refuse the same lists.
 */
const zSchoolBoards = z
  .array(zBoard, { error: 'Choose at least one board.' })
  .min(1, 'Choose at least one board.')
  .max(20)
  .transform((boards) => [...new Set(boards)]);

export const createTenantBody = z.object({
  name: z.string().trim().min(2).max(120),
  slug: zNewTenantSlug,
  signupPolicy: z.enum(SIGNUP_POLICIES).default('CLOSED'),

  // The boards this school teaches. It is given each board's whole Grade 10
  // and Grade 12 curriculum at once, and follows it: topics CWY adds later
  // arrive on their own. The school admin can still untick what they skip.
  boards: zSchoolBoards,

  // The first school admin, created in the same transaction as the tenant. A
  // school with no admin is a tenant nobody can administer without a platform
  // operator, so provisioning them together removes a state that has no owner.
  adminEmail: z.string().trim().email().max(255),
  adminName: z.string().trim().min(1).max(120),
});
export type CreateTenantBody = z.infer<typeof createTenantBody>;

export const updateTenantBody = z.object({
  // `slug` is deliberately absent. Renaming one would hand an old school's
  // bookmarks, audit trail and subdomain to whoever claimed the name next.
  name: z.string().trim().min(2).max(120).optional(),
  signupPolicy: z.enum(SIGNUP_POLICIES).optional(),
  teachersMayCreateStudents: z.boolean().optional(),
  teachersMayResetStudents: z.boolean().optional(),
  studentsMayEditOwnName: z.boolean().optional(),
  logoUrl: z.string().url().max(500).nullable().optional(),
  themeColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #2E4A1C.')
    .nullable()
    .optional(),
});
export type UpdateTenantBody = z.infer<typeof updateTenantBody>;

/**
 * `PATCH /platform/tenants/:id` — the school's settings, plus its boards.
 *
 * A separate schema rather than `boards` on `updateTenantBody`, because the
 * school's own settings route parses that one: boards are CWY's to set
 * (`setTenantBoards`), and a principal's settings form must not be able to
 * name one even by accident.
 */
export const platformUpdateTenantBody = updateTenantBody.extend({
  boards: zSchoolBoards.optional(),
});
export type PlatformUpdateTenantBody = z.infer<typeof platformUpdateTenantBody>;

export const tenantStatusBody = z.object({
  status: z.enum(['ACTIVE', 'SUSPENDED', 'ARCHIVED']),
  reason: z.string().trim().max(500).optional(),
});
export type TenantStatusBody = z.infer<typeof tenantStatusBody>;

/** Platform staff. Never creates a PLATFORM_SUPER_ADMIN — see the env bootstrap. */
export const createPlatformAdminBody = z.object({
  email: z.string().trim().email().max(255),
  name: z.string().trim().min(1).max(120),
});
export type CreatePlatformAdminBody = z.infer<typeof createPlatformAdminBody>;

/**
 * The roles CWY's own /admin console can create.
 *
 * Two genuinely different kinds of person in one list, because that is how the
 * console is used — "add someone to CWY" — not because they are the same thing:
 *
 *  - PLATFORM_ADMIN is authority over the PLATFORM. It is a `User.platformRole`
 *    and carries no membership anywhere, which is exactly why an operator cannot
 *    read inside any school.
 *  - TEACHER is a membership in the FIRST-PARTY tenant — CWY's own B2C workspace.
 *    They teach the direct-to-consumer students and have no platform authority at
 *    all.
 *
 * PLATFORM_SUPER_ADMIN is absent for the same reason it is absent above: there is
 * exactly one, it comes from the environment, and a screen that could mint
 * another would defeat keeping that credential out of the database.
 *
 * COUNSELLOR is absent because the role is not defined yet — there is no console,
 * no capability worth granting and no screen it is the answer to, so creating one
 * would produce an account whose only affordance is a roster it cannot act on.
 * The `MembershipRole` enum value STAYS, and every read path still handles it:
 * dropping it from the enum would need a destructive migration and would orphan
 * any membership already issued. Nobody can make a new one; existing ones work.
 * Reintroducing it here means giving it a console first.
 */
export const PLATFORM_STAFF_ROLES = ['PLATFORM_ADMIN', 'TEACHER'] as const;
export type PlatformStaffRole = (typeof PLATFORM_STAFF_ROLES)[number];

export const createPlatformStaffBody = z.object({
  email: z.string().trim().email().max(255),
  name: z.string().trim().min(1).max(120),
  role: z.enum(PLATFORM_STAFF_ROLES),
});
export type CreatePlatformStaffBody = z.infer<typeof createPlatformStaffBody>;

/**
 * Enable or disable one platform staff member.
 *
 * One verb for two mechanisms, chosen by what the target actually is: a platform
 * admin is banned at the user level, a first-party teacher has their membership
 * suspended. Exposing "enabled" rather than the mechanism keeps the console from
 * having to know which, and keeps a caller from picking the wrong one.
 */
export const platformStaffStatusBody = z.object({
  enabled: z.boolean(),
});
export type PlatformStaffStatusBody = z.infer<typeof platformStaffStatusBody>;

// ── Tenant: staff ───────────────────────────────────────────────────────────

/**
 * The roles a school console can give someone — on creation, and on a change.
 *
 * One list for both, because they are one question. STUDENT is excluded: a
 * student is created through the roster endpoint, which also builds their
 * profile, identifier and scope period, so a membership re-roled into STUDENT
 * would have no academic identity. COUNSELLOR is excluded for the reason given
 * at PLATFORM_STAFF_ROLES: the role has no console of its own yet. The enum
 * value survives for existing rows, which still display and still reset.
 *
 * The change route once took any MEMBERSHIP_ROLE, which is how a school admin
 * could still mint a counsellor by re-roling a teacher after creation had
 * stopped offering one.
 */
export const TENANT_STAFF_ROLES = ['SCHOOL_ADMIN', 'TEACHER'] as const;
export type TenantStaffRole = (typeof TENANT_STAFF_ROLES)[number];

const zTenantStaffRole = z.enum(TENANT_STAFF_ROLES, {
  error: 'A staff member can be a school admin or a teacher.',
});

/**
 * One classroom · subject a teacher takes, and which of its units.
 *
 * `unitIds: null` is the whole subject — one null-unit scope row, which keeps
 * meaning "all of it" when next year's syllabus adds a unit (AGENTS.md rule 5).
 * A list is that many unit rows. An EMPTY list would be a subject taught in no
 * part, which is not a thing a form should be able to send.
 */
export const teacherAssignment = z.object({
  classroomId: z.string().min(1),
  subjectId: z.string().min(1),
  unitIds: z
    .array(z.string().min(1))
    .min(1, 'Choose at least one unit, or all units.')
    .max(50)
    .nullable(),
});
export type TeacherAssignment = z.infer<typeof teacherAssignment>;

/**
 * What a teacher takes across the school, and the one class they look after.
 *
 * The per-teacher view of `ClassroomTeacherScope` — the same rows the classroom
 * card edits, read from the other side. It is not a second record of anything:
 * the server writes it into the scope table through the same normalization.
 */
export const teacherClassesBody = z.object({
  assignments: z.array(teacherAssignment).max(100).default([]),
  /** The classroom this teacher is class teacher of, or null for none. */
  classTeacherOf: z.string().min(1).nullable().default(null),
});
export type TeacherClassesBody = z.infer<typeof teacherClassesBody>;

export const createStaffBody = z
  .object({
    email: z.string().trim().email().max(255),
    name: z.string().trim().min(1).max(120),
    role: zTenantStaffRole,
    // Optional, and only for a teacher. What they take is still decided per
    // classroom and stored only in ClassroomTeacherScope — the form just lets
    // the person adding a teacher say it in the same breath.
    assignments: z.array(teacherAssignment).max(100).optional(),
    classTeacherOf: z.string().min(1).nullable().optional(),
  })
  .refine(
    (body) => body.role === 'TEACHER' || (!body.assignments?.length && !body.classTeacherOf),
    { error: 'Only a teacher is given classes.', path: ['assignments'] },
  );
export type CreateStaffBody = z.infer<typeof createStaffBody>;

/**
 * One row of a staff import — the CSV a school uses to bring its teachers in
 * at once instead of one form at a time.
 *
 * A teacher's classes are named the way a school's own timetable names them: a
 * class CODE (the one the student import uses) and subject NAMES, which the
 * server resolves against that class's own cohort and year. Each row gives one
 * class; a teacher who takes several repeats their email on several rows, and
 * the rows are combined. A subject named here is the whole subject — units are
 * narrowed afterwards on the staff page, which is where a picker can show them.
 */
export const importStaffRow = z
  .object({
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().email().max(255),
    role: zTenantStaffRole,
    classroomCode: z.string().trim().min(1).max(80).optional(),
    subjects: z.array(z.string().trim().min(1).max(120)).max(20).default([]),
    classTeacher: z.boolean().default(false),
  })
  .refine(
    (row) =>
      row.role === 'TEACHER' || (!row.classroomCode && !row.subjects.length && !row.classTeacher),
    { error: 'Only a teacher is given classes.', path: ['classroomCode'] },
  )
  .refine((row) => Boolean(row.classroomCode) || (!row.subjects.length && !row.classTeacher), {
    error: 'Give the class code that the subjects or class teacher are for.',
    path: ['classroomCode'],
  });
export type ImportStaffRow = z.infer<typeof importStaffRow>;

export const bulkImportStaffBody = z.object({
  staff: z.array(importStaffRow).min(1).max(200),
});
export type BulkImportStaffBody = z.infer<typeof bulkImportStaffBody>;

export const updateMembershipBody = z.object({
  role: zTenantStaffRole.optional(),
  status: z.enum(['ACTIVE', 'SUSPENDED', 'LEFT']).optional(),
});
export type UpdateMembershipBody = z.infer<typeof updateMembershipBody>;

// ── Tenant: students ────────────────────────────────────────────────────────

/**
 * One rostered student.
 *
 * `email` is optional and that is the substantive difference from B2C signup: a
 * school student may have no address at all. When absent the credential adapter
 * mints a reserved `.invalid` address that is never displayed and never
 * delivered to.
 */
export const createStudentBody = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(255).optional(),
  loginId: zLoginIdentifier,
  // The classroom states the cohort. Board, grade and section are read from it
  // rather than sent alongside, because two statements of the same fact in one
  // request need a rule for which wins, and nobody writes that rule down.
  classroomId: z.string().min(1),
  admissionNumber: z.string().trim().max(64).optional(),
  rollNumber: z.string().trim().max(64).optional(),
  parentEmail: z.string().trim().email().max(255).optional(),
  // Required, exactly as in the import. The staff member issuing the account is
  // the one writing it on the slip they hand over, and a generated password
  // they then have to transcribe back is the step that does not happen.
  password: zPassword,
});
export type CreateStudentBody = z.infer<typeof createStudentBody>;

/**
 * Roster import — one line per student, four comma-separated fields:
 *
 *   Aarav Sharma, cbse-grade-10_a, 01, first-pass-2026
 *
 * The classroom CODE, not an id: an admin is retyping from a spreadsheet, and a
 * cuid is not something anyone types. Board, grade and section are read from the
 * classroom the code names rather than repeated per row — a row that disagreed
 * with its own classroom would have to be resolved by a rule nobody wrote down.
 *
 * The school supplies a permanent sign-in ID. Returning students keep their
 * existing account and password. For new students the supplied password is
 * temporary in the only sense that matters — `mustChangePassword` is set, and
 * the student replaces it at first sign-in.
 *
 * New students run through the same domain service as single creation. Existing
 * IDs are matched within the school and re-enrolled without recreating people.
 * Capped so one request cannot hold the server indefinitely.
 */
export const importStudentRow = z.object({
  name: z.string().trim().min(1).max(120),
  classroomCode: z.string().trim().min(1).max(80),
  loginId: zLoginIdentifier,
  password: zPassword,
});
export type ImportStudentRow = z.infer<typeof importStudentRow>;

export const bulkImportStudentsBody = z.object({
  students: z.array(importStudentRow).min(1).max(500),
});
export type BulkImportStudentsBody = z.infer<typeof bulkImportStudentsBody>;

export const updateStudentBody = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  board: zBoard.optional(),
  grade: zGrade.optional(),
  section: z.string().trim().max(20).nullable().optional(),
  admissionNumber: z.string().trim().max(64).nullable().optional(),
  rollNumber: z.string().trim().max(64).nullable().optional(),
  parentEmail: z.string().trim().email().max(255).nullable().optional(),
});
export type UpdateStudentBody = z.infer<typeof updateStudentBody>;

/**
 * Suspend or restore one student. Its own body rather than a field on
 * `updateStudentBody`, because losing access revokes sessions and is audited as
 * an access change — not something to arrive alongside a corrected roll number.
 */
export const studentStatusBody = z.object({
  status: z.enum(['ACTIVE', 'SUSPENDED']),
});
export type StudentStatusBody = z.infer<typeof studentStatusBody>;

// ── Tenant: classrooms ──────────────────────────────────────────────────────

/**
 * A classroom is a board, a grade and — optionally — a section. Its `name` and
 * `code` are DERIVED from those by `classroomName()` / `classroomCode()` and are
 * deliberately not accepted here: the code is what a roster import names, and a
 * hand-typed one would be free to disagree with the grade it claims to be.
 *
 * `academicYear` is absent for the same reason. A classroom is created into the
 * active year, which the server resolves.
 */
export const createClassroomBody = z.object({
  board: zBoard,
  grade: zGrade,
  section: z.string().trim().max(20).optional(),
});
export type CreateClassroomBody = z.infer<typeof createClassroomBody>;

/**
 * What one teacher teaches in one classroom. A null `unitId` means the whole
 * subject — see the ClassroomTeacherScope model for why that is not the same as
 * listing every unit.
 */
export const classroomTeacherScopeBody = z.object({
  scopes: z
    .array(
      z.object({
        membershipId: z.string().min(1),
        subjectId: z.string().min(1),
        unitId: z.string().min(1).nullable().default(null),
      }),
    )
    .max(200),
});
export type ClassroomTeacherScopeBody = z.infer<typeof classroomTeacherScopeBody>;

/**
 * Who looks after this class. A relationship to ONE room, not a role: the class
 * teacher reads every subject's work here and manages its roster, and nothing
 * about them changes in the class next door. Null clears it.
 */
export const classTeacherBody = z.object({
  membershipId: z.string().min(1).nullable(),
});
export type ClassTeacherBody = z.infer<typeof classTeacherBody>;

export const classroomEnrollBody = z.object({
  membershipIds: z.array(z.string().min(1)).min(1).max(500),
});
export type ClassroomEnrollBody = z.infer<typeof classroomEnrollBody>;

// ── Authentication ──────────────────────────────────────────────────────────

/**
 * School sign-in.
 *
 * Carries no tenant field: the tenant comes from the resolved request context
 * (the `/school/:slug` path or the tenant header), never from the body. If the
 * body could name its own tenant, `1234` + a guessed slug would be a login
 * attempt against every school at once.
 */
export const schoolLoginBody = z.object({
  loginId: zLoginIdentifier,
  password: z.string().min(1).max(200),
});
export type SchoolLoginBody = z.infer<typeof schoolLoginBody>;

export const changePasswordBody = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: zPassword,
});
export type ChangePasswordBody = z.infer<typeof changePasswordBody>;

/**
 * Staff issuing a new temporary password for a tenant-managed student.
 *
 * Deliberately has no `password` field. Letting staff choose means a whole
 * school gets `school@123`, and the point of a temporary credential is that it
 * is random, shown once and forced to change. The server generates it.
 */
export const resetStudentCredentialBody = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type ResetStudentCredentialBody = z.infer<typeof resetStudentCredentialBody>;

/** Roster activation: a student claims their account with the issued code. */
export const activateStudentBody = z.object({
  loginId: zLoginIdentifier,
  activationCode: z.string().trim().min(6).max(64),
  newPassword: zPassword,
});
export type ActivateStudentBody = z.infer<typeof activateStudentBody>;

/**
 * Converting a school-managed credential to a self-managed one.
 *
 * One-way and deliberately explicit: after this the school can no longer reset
 * the password, so it cannot be a side effect of an ordinary profile save.
 */
export const convertCredentialBody = z.object({
  email: z.string().trim().email().max(255),
  newPassword: zPassword,
});
export type ConvertCredentialBody = z.infer<typeof convertCredentialBody>;

// ── Audit ───────────────────────────────────────────────────────────────────

export const auditLogQuery = z.object({
  action: z.string().max(64).optional(),
  targetUserId: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(64).optional(),
});
export type AuditLogQuery = z.infer<typeof auditLogQuery>;

// ── Roster ──────────────────────────────────────────────────────────────────

/**
 * `GET /tenant-admin/students` — search, class filter and a cursor. Filtering
 * is the server's, so a school past one page is still searchable as a whole.
 * `classroomId` selects a CANDIDATE; the route re-loads it with the tenant.
 */
export const tenantStudentsQuery = z.object({
  q: z.string().trim().max(120).optional(),
  classroomId: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(64).optional(),
});
export type TenantStudentsQuery = z.infer<typeof tenantStudentsQuery>;

// ── Curriculum ──────────────────────────────────────────────────────────────
//
// Two audiences, deliberately different verbs. The platform AUTHORS the tree
// (`manage:curriculum`); a school SELECTS from it (`select:curriculum`). None of
// these accept a key or a slug for an existing node — a key is derived from the
// path by the server, never sent, or a client could tag content against a node
// that does not exist.

export const curriculumTreeQuery = z.object({
  board: zBoard.optional(),
  grade: zGrade.optional(),
  /** Defaults to the active year. */
  academicYearId: z.string().min(1).optional(),
});
export type CurriculumTreeQuery = z.infer<typeof curriculumTreeQuery>;

/**
 * Create a node. `parentId` is read against `level`, so a chapter cannot be
 * hung off a subject by passing the wrong id — the server loads the parent at
 * the level it claims and 404s otherwise.
 *
 * A subject's parent is the academic year, and it additionally needs a board
 * and grade — codes that must already exist as rows (`/curriculum/levels`).
 */
export const CURRICULUM_LEVELS = ['SUBJECT', 'UNIT', 'CHAPTER', 'SUBTOPIC'] as const;
export type CurriculumLevel = (typeof CURRICULUM_LEVELS)[number];

export const createCurriculumNodeBody = z.object({
  level: z.enum(CURRICULUM_LEVELS),
  parentId: z.string().min(1),
  name: z.string().trim().min(1).max(160),
  board: zBoard.optional(),
  grade: zGrade.optional(),
});
export type CreateCurriculumNodeBody = z.infer<typeof createCurriculumNodeBody>;

/**
 * Rename a node. Only the display name — a slug is a path segment baked into
 * every descendant's key and into the conceptKey of every question beneath it,
 * so renaming one would silently detach content.
 */
export const renameCurriculumNodeBody = z.object({
  level: z.enum(CURRICULUM_LEVELS),
  name: z.string().trim().min(1).max(160),
});
export type RenameCurriculumNodeBody = z.infer<typeof renameCurriculumNodeBody>;

/**
 * A board or a grade — the two levels above the year-versioned tree, and the
 * only ones that are not versioned by year: CBSE is CBSE in every year.
 *
 * The code is upper-cased here, so "cbse" and "CBSE" cannot become two boards,
 * and it is permanent: every curriculum key is built from it. A numbered grade
 * should be `G<n>` ("G9"), which keys and headings read as "9".
 */
export const CURRICULUM_LEVEL_KINDS = ['BOARD', 'GRADE'] as const;
export type CurriculumLevelKind = (typeof CURRICULUM_LEVEL_KINDS)[number];

export const createCurriculumLevelBody = z.object({
  kind: z.enum(CURRICULUM_LEVEL_KINDS),
  code: z
    .string()
    .trim()
    .transform((code) => code.toUpperCase())
    .pipe(
      z
        .string()
        .regex(LEVEL_CODE_RE, {
          error: 'Use capital letters, digits and underscores, starting with a letter — e.g. IB or G9.',
        }),
    ),
  label: z.string().trim().min(1).max(60),
});
export type CreateCurriculumLevelBody = z.infer<typeof createCurriculumLevelBody>;

export const renameCurriculumLevelBody = z.object({
  label: z.string().trim().min(1).max(60),
});

/** `DELETE /curriculum/nodes/:id?level=` — the level the id is claimed to be at. */
export const curriculumNodeLevelQuery = z.object({
  level: z.enum(CURRICULUM_LEVELS),
});

/**
 * What a school runs. Sent as the COMPLETE intended selection rather than a
 * diff: the form knows what it means, and a diff would depend on the client's
 * view of current state being fresh — which, on a page left open, it is not.
 */
export const curriculumSelectionBody = z.object({
  grades: z.array(z.object({ board: zBoard, grade: zGrade })).max(40),
  chapterIds: z.array(z.string().min(1)).max(2000),
  // Sub-topics skipped inside offered chapters. Optional: a caller that only
  // knows chapters still saves, and clears no skip it never showed.
  excludedSubtopicIds: z.array(z.string().min(1)).max(10000).optional(),
});
export type CurriculumSelectionBody = z.infer<typeof curriculumSelectionBody>;

/**
 * `?year=` on the teacher console's lists. Absent means the school's current
 * year; a previous year is read-only, which the server enforces, not the form.
 */
export const teacherYearQuery = z.object({
  year: z.string().min(1).max(64).optional(),
});
